import { IconeCalendario, IconeChamado, IconeRelatorio } from '../../components/Icones'
import { cn } from '../../lib/cn'
import {
  ROTULO_CURTO_ORIGEM_CHAMADO,
  ROTULO_ORIGEM_CHAMADO,
  ROTULO_ORIGEM_DEMANDA,
  ROTULO_SITUACAO_SLA,
  TERMOS,
} from '../../lib/glossario'
import type { OrigemDaDemanda, OrigemDoChamado, SituacaoDeSla } from '../../types/chamado'

/**
 * As etiquetas do módulo de chamados.
 *
 * Ficam juntas porque a lista, o cartão do celular e o detalhe têm de marcar as mesmas coisas do
 * mesmo jeito: se o cartão do telefone esquecer a procedência, o fiscal que confere pelo celular lê
 * dado de planilha como se fosse leitura do e-Niterói — e as duas fontes discordam por construção.
 */

const BASE =
  'inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset'

/**
 * Cor por situação de prazo, sem `default` silencioso.
 *
 * **`SEM_PRAZO` é cinza neutro, e isso é a decisão mais importante deste arquivo.** Verde ali faria
 * a tela afirmar cumprimento de um prazo que não existe; vermelho afirmaria um atraso que ninguém
 * mediu. São 12 dos 64 chamados medidos no e-Niterói e 7 dos 70 da planilha de maio — a própria
 * planilha os tira do denominador, ao reportar "base mensurável de SLA: 63" sobre 70 fechados. O
 * cinza diz o que a coisa é: fora da medição.
 */
const COR_DA_SITUACAO: Record<SituacaoDeSla, string> = {
  DENTRO_DO_PRAZO: 'bg-green-50 text-green-900 ring-green-200',
  ESTOURADO: 'bg-red-50 text-red-900 ring-red-200',
  SEM_PRAZO: 'bg-slate-100 text-slate-700 ring-slate-300',
}

/**
 * Situação do chamado diante do prazo.
 *
 * O glossário vem primeiro e o `situacaoSlaRotulo` do servidor é a rede de segurança, na mesma
 * ordem de `EtiquetaDeStatus`: com o servidor ganhando, renomear o termo em `glossario.ts` mudaria
 * uma tela e deixaria as outras como estavam, sem erro nenhum à vista.
 *
 * Em `SEM_PRAZO` a etiqueta carrega a explicação junto, no `<title>` do ícone — é o nome que o
 * leitor de tela anuncia e a dica que o navegador mostra ao passar o mouse. Sem ela, "sem prazo
 * definido" numa coluna chamada "Prazo" se lê como dado faltando.
 *
 * @param props a situação e o rótulo que o servidor mandou junto
 * @returns a etiqueta da situação
 */
export function EtiquetaDeSla({ situacao, rotulo }: { situacao: SituacaoDeSla; rotulo?: string }) {
  return (
    <span className={cn(BASE, COR_DA_SITUACAO[situacao])}>
      {situacao === 'SEM_PRAZO' && <IconeCalendario className="h-3.5 w-3.5" titulo={TERMOS.semPrazoDeSla.ajuda} />}
      {ROTULO_SITUACAO_SLA[situacao] ?? rotulo ?? situacao}
    </span>
  )
}

/**
 * Por qual porta a demanda entrou — e só quando ela NÃO entrou pela porta normal.
 *
 * Não existe etiqueta para sustentação: marcar o que é normal não informa nada e vira ruído em toda
 * linha. Quem precisa saltar aos olhos é o projeto, porque é ele que muda denominador — pela
 * premissa em vigor, conta para conformidade e aceite e fica fora do indicador de prazos da
 * sustentação. São 3 dos 64 chamados medidos, todos do grupo "Projetos E-cidade", e a lista fixa de
 * grupos do sistema anterior não os enxergava.
 *
 * @param props a origem da demanda
 * @returns a marca de projeto, ou nada
 */
export function EtiquetaDeOrigemDaDemanda({ origem }: { origem: OrigemDaDemanda }) {
  if (origem !== 'PROJETO') {
    return null
  }
  return (
    <span className={cn(BASE, 'bg-purple-50 text-purple-900 ring-purple-200')}>
      <IconeRelatorio className="h-3.5 w-3.5" />
      {ROTULO_ORIGEM_DEMANDA.PROJETO}
    </span>
  )
}

/**
 * De onde o registro veio.
 *
 * Ao contrário da procedência de GMUD — em que só o histórico importado é marcado, porque o
 * registro do processo novo é o normal —, aqui as DUAS aparecem sempre. Não há fonte "normal": a
 * planilha traz o julgamento do fiscal, que nenhuma leitura automática produz, e a leitura do
 * e-Niterói traz o estado do chamado no instante da consulta. Medido: a planilha diz que o chamado
 * 2026050648 fechou em 29/05 com prazo estourado e o e-Niterói diz que ele está pendente. As duas
 * estão certas no seu instante, e uma etiqueta só em metade delas produziria a leitura de que a
 * outra metade é "o dado do sistema".
 *
 * @param props a procedência do registro e o rótulo que o servidor mandou junto
 * @returns a etiqueta de procedência
 */
export function EtiquetaDeProcedencia({
  origem,
  rotulo,
  curta = false,
}: {
  origem: OrigemDoChamado
  rotulo?: string
  /** Na coluna da tabela: o nome da fonte só, com o rótulo inteiro no `title`. */
  curta?: boolean
}) {
  const completo = ROTULO_ORIGEM_CHAMADO[origem] ?? rotulo ?? origem
  return (
    <span
      className={cn(BASE, 'whitespace-nowrap bg-slate-100 text-slate-700 ring-slate-200')}
      title={curta ? completo : undefined}
    >
      <IconeChamado className="h-3.5 w-3.5" />
      {curta ? (ROTULO_CURTO_ORIGEM_CHAMADO[origem] ?? completo) : completo}
    </span>
  )
}
